import {
  IcCashierDiscount,
  IcFee,
  IcPaymentMethod,
  IcPaymentTypeLoyaltyPoint,
  Text,
} from '@fozito/common-ui';
import React, { ReactNode, SVGProps } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import storage from 'utils/sessionStorage';

interface TabItem {
  path: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  label: string;
}

interface StyledTabProps {
  $isActive: boolean;
}

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const Navigation = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border-right: 1px solid #e5e7eb;
  height: 100%;
  background-color: white;
  width: 300px;
`;

const TabLink = styled(Link)<StyledTabProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 2px;
  text-decoration: none;
  min-width: 280px;
  background-color: ${({ $isActive }) =>
    $isActive ? '#DC5500' : 'transparent'};
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${({ $isActive }) => ($isActive ? '#DC5500' : '#f3f4f6')};
  }
`;

const Icon = styled.span`
  font-size: 18px;
`;

const Content = styled.div`
  flex: 1;
`;

const GeneralConfigLayout: React.FC = () => {
  const location = useLocation();
  const shopId = storage.shop_id.get();
  const isActiveTab = (path: string): boolean => {
    return location.pathname === path;
  };

  const tabs: TabItem[] = [
    {
      path: `/store/${shopId}/setting/general_configuration`,
      icon: <IcFee />,
      activeIcon: <IcFee fill='#FFF' />,
      label: 'Tax',
    },
    {
      path: `/store/${shopId}/setting/general_configuration/cash_discount`,
      icon: <IcCashierDiscount />,
      activeIcon: <IcCashierDiscount fill='#FFF' />,
      label: 'Cash Discount',
    },
    {
      path: `/store/${shopId}/setting/general_configuration/fee_credit_cash`,
      icon: <IcPaymentMethod />,
      activeIcon: <IcPaymentMethod fill='#FFF' />,
      label: 'Fee Credit Card',
    },
    {
      path: `/store/${shopId}/setting/general_configuration/loyalty`,
      icon: <IcPaymentTypeLoyaltyPoint />,
      activeIcon: <IcPaymentTypeLoyaltyPoint fill='#FFF' />,
      label: 'Loyalty Rewards',
    },
    {
      path: `/store/${shopId}/setting/general_configuration/referral`,
      icon: <IcReferral width={24} height={24}/>,
      activeIcon: <IcReferral width={24} height={24} fill='#FFF' />,
      label: 'Referral',
    },
  ];

  return (
    <Container>
      <Row>
        <Navigation>
          {tabs.map((tab) => (
            <TabLink
              key={tab.path}
              to={tab.path}
              $isActive={isActiveTab(tab.path)}
            >
              {isActiveTab(tab.path) ? (
                <>
                  <Icon>{tab.activeIcon}</Icon>
                  <Text title_h5_24_semibold color_text_1>
                    {tab.label}
                  </Text>
                </>
              ) : (
                <>
                  <Icon>{tab.icon}</Icon>
                  <Text title_h5_24_semibold color_text_5>
                    {tab.label}
                  </Text>
                </>
              )}
            </TabLink>
          ))}
        </Navigation>

        <Content>
          <Outlet />
        </Content>
      </Row>
    </Container>
  );
};

export default GeneralConfigLayout;

const IcReferral = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={32}
    height={33}
    viewBox='0 0 32 33'
    fill='none'
    {...props}
  >
    <path
      fill={props?.fill || '#1D2129'}
      d='M6.667 30.146a5.334 5.334 0 0 0 5.144-4h8.378a5.39 5.39 0 1 0 3.72-6.45l-4.154-7.102a5.333 5.333 0 1 0-7.507 0l-4.144 7.107a5.28 5.28 0 0 0-1.437-.222 5.333 5.333 0 1 0 0 10.667Zm18.666-8a2.667 2.667 0 1 1 0 5.333 2.667 2.667 0 0 1 0-5.333ZM16 6.146a2.667 2.667 0 1 1 0 5.333 2.667 2.667 0 0 1 0-5.333Zm-1.437 7.779c.466.14.95.214 1.437.221a5.214 5.214 0 0 0 1.443-.224l4.15 7.097a5.333 5.333 0 0 0-1.404 2.46h-8.378a5.325 5.325 0 0 0-1.392-2.45l4.144-7.104Zm-7.896 8.221a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334Z'
    />
  </svg>
);
